import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaArrowUpRightFromSquare } from "react-icons/fa6";
import { FiAlertCircle } from "react-icons/fi";
import { RiRefreshLine } from "react-icons/ri";
import type { PayoutAccount } from "./payoutData";
import PayoutKpiValue from "./PayoutKpiValue";
import { accountFromApiRecord } from "./api/payoutApiAdapters";
import { isPayoutApiMode, payoutApi } from "./api/payoutApiClient";
import { usePayoutResource } from "./api/usePayoutResource";

type RechargeAccountDetails = {
  bankName: string;
  holderName: string;
  accountNumber: string;
  ifsc: string;
};

type OverviewAccount = PayoutAccount & {
  rechargeAccount: RechargeAccountDetails;
  balanceAsOf?: string;
};

function BalanceRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-4 text-sm">
      <span className="text-[#505E8E]">{label}</span>
      <span className="font-semibold text-[#1B163B] text-right">{value}</span>
    </div>
  );
}

function RechargeDetail({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[180px_1fr] items-center gap-8">
      <span className="text-sm font-medium text-[#505E8E]">{label}</span>
      <span className="text-sm font-semibold text-[#1B163B]">{value}</span>
    </div>
  );
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const pickString = (
  record: Record<string, unknown>,
  keys: string[],
  fallback = "",
) => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) return value;
    if (typeof value === "number") return String(value);
  }
  return fallback;
};

const emptyRechargeAccount = (): RechargeAccountDetails => ({
  bankName: "",
  holderName: "",
  accountNumber: "",
  ifsc: "",
});

const displayValue = (value?: string | number | boolean) => {
  if (value === undefined || value === null || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const mergeAccountOverviewPayload = (
  overview: Record<string, unknown>,
  balance: Record<string, unknown> | null,
) => {
  const account = asRecord(overview.account);
  const overviewBalance = asRecord(overview.balance);
  const liveBalance = asRecord(balance);
  const nestedLiveBalance = asRecord(liveBalance.balance);

  return {
    ...overview,
    ...account,
    ...overviewBalance,
    ...liveBalance,
    ...nestedLiveBalance,
  };
};

const rechargeAccountFromPayload = (
  overview: Record<string, unknown>,
  account: PayoutAccount,
): RechargeAccountDetails => {
  const activity = asRecord(overview.activity);
  const extras = asRecord(overview.extras);
  const recharge = asRecord(activity.recharge_account);
  const virtualAccount = asRecord(extras.virtual_account);
  const source = Object.keys(recharge).length ? recharge : virtualAccount;
  const overviewAccount = asRecord(overview.account);

  return {
    bankName:
      pickString(source, ["bank_name", "bankName"], "") ||
      account.bank,
    holderName:
      pickString(source, ["holder_name", "holderName", "beneficiary_name", "beneficiaryName"], "") ||
      pickString(overviewAccount, ["account_holder_name", "accountHolderName"], ""),
    accountNumber:
      pickString(source, ["account_number", "accountNumber", "va_number", "vaNumber"], "") ||
      account.accountNumber,
    ifsc:
      pickString(source, ["ifsc", "ifsc_code", "ifscCode"], "") ||
      account.ifsc,
  };
};

function PayoutAccountOverview() {
  const { accountId } = useParams();
  const defaultAccount = React.useMemo<OverviewAccount>(
    () => ({
      id: accountId || "",
      name: "",
      school: "",
      bank: "",
      logoUrl: "",
      accountNumber: "",
      ifsc: "",
      currency: "INR",
      addedOn: "",
      accountType: "",
      holderName: "",
      branchName: "",
      branchCity: "",
      useForPayouts: false,
      useForSettlements: false,
      primary: false,
      status: "",
      createdBy: "",
      verificationStatus: "",
      availableBalance: 0,
      accountBalance: 0,
      fundsOnHold: 0,
      overdraftBalance: 0,
      overdraftLimit: 0,
      lowBalanceThreshold: 0,
      accent: "",
      rechargeAccount: emptyRechargeAccount(),
    }),
    [accountId],
  );
  const requestedAccountId = accountId || "";

  const accountResource = usePayoutResource(
    defaultAccount,
    async (signal) => {
      const [accounts, overview, balance] = await Promise.all([
        payoutApi.accounts(signal).catch(() => []),
        payoutApi.accountOverview(requestedAccountId, signal),
        payoutApi.accountBalance(requestedAccountId, signal).catch(() => null),
      ]);
      const listAccount =
        accounts.find((row) =>
          [row.id, row.account_id, row.accountId, row.funding_account_id, row.fundingAccountId]
            .map((value) => (value == null ? "" : String(value)))
            .includes(requestedAccountId),
        ) || {};
      const mergedOverview = {
        ...overview,
        account: {
          ...asRecord(listAccount),
          ...asRecord(overview.account),
        },
      };
      const mergedPayload = mergeAccountOverviewPayload(mergedOverview, balance);
      const mappedAccount = accountFromApiRecord(mergedPayload, defaultAccount);
      return {
        ...mappedAccount,
        rechargeAccount: rechargeAccountFromPayload(mergedOverview, mappedAccount),
        balanceAsOf: pickString(
          mergedPayload,
          ["as_of", "asOf", "balance_as_of", "balanceAsOf"],
          "",
        ),
      };
    },
    [requestedAccountId, defaultAccount],
  );
  const account = accountResource.data;
  const rechargeAccount = account.rechargeAccount || emptyRechargeAccount();

  if (!requestedAccountId && !isPayoutApiMode()) {
    return <Navigate to="/payouts/dashboard" replace />;
  }

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <Link
        to="/payouts/dashboard"
        className="mb-5 inline-flex items-center gap-2 text-sm font-semibold text-[#6687FF]"
      >
        <FaArrowLeft className="text-xs" />
        Back to Dashboard
      </Link>

      <div className="mb-5 rounded-[8px] border border-[#E0E5F2] bg-white px-6 py-5 new-shadow">
        <div className="flex items-center justify-between gap-8">
          <div className="flex min-w-0 items-center gap-6">
            <div className="flex h-[64px] w-[64px] shrink-0 items-center justify-center overflow-hidden rounded-[6px] bg-white">
              <img
                src={account.logoUrl}
                alt={`${account.bank} logo`}
                className="h-full w-full object-contain"
              />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-3">
                <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
                  {account.name}
                </h1>
                <span className="rounded-[4px] bg-[#DCFCE7] px-2 py-1 text-xs font-semibold text-[#16A34A]">
                  {displayValue(account.status || "Active")}
                </span>
              </div>
              <p className="mt-2 text-sm font-medium text-[#505E8E]">
                {account.bank}
              </p>
              <p className="mt-2 text-sm font-semibold text-[#1B163B]">
                {account.accountNumber}
                <span className="mx-3 font-normal text-[#A3AED0]">|</span>
                <span className="font-medium text-[#505E8E]">IFSC:</span>{" "}
                {account.ifsc}
              </p>
            </div>
          </div>

          <div className="ml-auto flex items-center gap-20">
            <div>
              <p className="text-xs font-medium text-[#505E8E]">Currency</p>
              <p className="mt-2 text-sm font-semibold text-[#1B163B]">
                {account.currency}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-[#505E8E]">Added On</p>
              <p className="mt-2 text-sm font-semibold text-[#1B163B]">
                {account.addedOn}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-5 rounded-[8px] border border-[#F6B44B] bg-[#FFF7ED] px-5 py-4 text-sm font-medium text-[#505E8E]">
        <div className="flex gap-3">
          <FiAlertCircle className="mt-0.5 shrink-0 text-lg text-[#D97706]" />
          <p className="leading-6">
            Please note, any additional funds parked in your wallet will be
            swept out to your account on a weekly basis.
            <br />
            Please write to{" "}
            <a
              href="mailto:contact@clearn.in"
              className="font-semibold text-[#6687FF] underline"
            >
              contact@clearn.in
            </a>{" "}
            for more details.
          </p>
        </div>
      </div>

      <div className="mb-5">
        <div className="grid gap-5 lg:grid-cols-2">
          <div className="min-h-[280px] rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
            <div className="flex justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-[#505E8E]">
                  Available Balance
                </p>
                <p className="mt-4 text-[28px] font-normal leading-9 text-[#1B163B]">
                  ₹<PayoutKpiValue value={account.availableBalance} />
                </p>
              </div>
              <button className="flex h-8 w-8 items-center justify-center rounded-[6px] bg-[#6687FF1A] text-[#6687FF]">
                <FaArrowUpRightFromSquare className="text-xs" />
              </button>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-[#505E8E]">
                {account.balanceAsOf
                  ? `Last updated at ${account.balanceAsOf}`
                  : "Last updated from balance API"}
              </p>
              <button className="flex items-center gap-2 text-sm font-semibold text-[#6687FF]">
                <RiRefreshLine className="text-base" />
                Refresh
              </button>
            </div>
            <div className="mt-5 space-y-4 border-t border-[#E0E5F2] pt-5">
              <BalanceRow
                label="Account Balance"
                value={<>₹<PayoutKpiValue value={account.accountBalance} /></>}
              />
              <BalanceRow
                label="Funds on Hold"
                value={<>₹<PayoutKpiValue value={account.fundsOnHold} /></>}
              />
              <BalanceRow
                label="Overdraft Balance"
                value={<>₹<PayoutKpiValue value={account.overdraftBalance} /></>}
              />
            </div>
          </div>

          <div className="min-h-[280px] rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
            <p className="text-sm font-semibold text-[#505E8E]">
              Overdraft Limit
            </p>
            <p className="mt-4 text-[28px] font-normal leading-9 text-[#1B163B]">
              NA
            </p>
            <p className="mt-4 text-sm font-medium text-[#505E8E]">
              This amount cannot be withdrawn{" "}
              <button className="font-semibold text-[#6687FF] underline">
                Know more
              </button>
            </p>
          </div>

        </div>
      </div>

      <div className="mb-5 grid gap-5 xl:grid-cols-3">
        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <h2 className="mb-5 text-[18px] font-semibold text-[#1B163B]">
            Account Details
          </h2>
          <div className="space-y-4">
            <RechargeDetail label="Account Nickname" value={displayValue(account.name)} />
            <RechargeDetail label="Account Type" value={displayValue(account.accountType)} />
            <RechargeDetail label="School / Entity" value={displayValue(account.school)} />
            <RechargeDetail label="Currency" value={displayValue(account.currency)} />
          </div>
        </section>

        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <h2 className="mb-5 text-[18px] font-semibold text-[#1B163B]">
            Bank Information
          </h2>
          <div className="space-y-4">
            <RechargeDetail label="Bank Name" value={displayValue(account.bank)} />
            <RechargeDetail label="Account Holder Name" value={displayValue(account.holderName)} />
            <RechargeDetail label="Account Number" value={displayValue(account.accountNumber)} />
            <RechargeDetail label="IFSC Code" value={displayValue(account.ifsc)} />
            <RechargeDetail label="Branch Name" value={displayValue(account.branchName)} />
            <RechargeDetail label="Branch City" value={displayValue(account.branchCity)} />
          </div>
        </section>

        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <h2 className="mb-5 text-[18px] font-semibold text-[#1B163B]">
            Usage & Controls
          </h2>
          <div className="space-y-4">
            <RechargeDetail label="Use for Payouts" value={displayValue(account.useForPayouts)} />
            <RechargeDetail label="Use for Settlements" value={displayValue(account.useForSettlements)} />
            <RechargeDetail label="Primary Account" value={displayValue(account.primary)} />
            <RechargeDetail label="Status" value={displayValue(account.status)} />
            <RechargeDetail label="Verification" value={displayValue(account.verificationStatus)} />
            <RechargeDetail label="Created By" value={displayValue(account.createdBy)} />
          </div>
        </section>
      </div>

      <div>
        <h2 className="text-[18px] font-semibold text-[#1B163B]">
          Recharge Account Details
        </h2>
        <p className="mb-5 mt-2 text-sm font-medium text-[#505E8E]">
          To add funds to your Payouts A/c, transfer funds via RTGS/NEFT/IMPS to
          either of the accounts below.
        </p>
        <div className="max-w-[980px] rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <div className="grid grid-cols-2 gap-x-16 gap-y-6">
            <RechargeDetail
              label="Bank Name"
              value={
                <span className="inline-flex items-center gap-2">
                  <span>
                    <span className="mb-1 block w-fit rounded-[3px] bg-[#6687FF] px-2 py-0.5 text-[10px] font-semibold text-white">
                      Recommended
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <img
                        src={account.logoUrl}
                        alt={`${account.bank} logo`}
                        className="h-7 w-16 object-contain"
                      />
                      {rechargeAccount.bankName}
                    </span>
                  </span>
                </span>
              }
            />
            <RechargeDetail
              label="A/c Number"
              value={rechargeAccount.accountNumber}
            />
            <RechargeDetail
              label="A/c Holder's Name"
              value={rechargeAccount.holderName}
            />
            <RechargeDetail label="IFSC" value={rechargeAccount.ifsc} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default PayoutAccountOverview;
